import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {OttopilotEventContainerStatus} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainer";
import {OttopilotEventStatus} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEvent";
import {z as zod} from "zod";

export enum EventContainerMovementOuScope {
    currentOrganizationalUnitOnly = "currentOrganizationalUnitOnly",
    anyConfiguredTarget = "anyConfiguredTarget",
}

export enum EventContainerMovementTargetUserOuMembership {
    notRequired = "notRequired",
    required = "required",
}

export type EventContainerMovementBusinessStatusRule = {
    columnId: Uuid;
    eligibleValues: Array<Uuid>;
};

export type EventContainerMovementHierarchyField = {
    eventContainerColumnId: Uuid;
    organizationalUnitTemplateId: Uuid;
};

export type EventContainerMovementDependentEventFieldMapping = {
    eventColumnId: Uuid;
    movementField: Uuid;
};

export type EventContainerMovementDependentEventConfiguration = {
    businessStatusRule?: EventContainerMovementBusinessStatusRule;
    eligibleSystemStatuses: Array<OttopilotEventStatus>;
    eventTemplateId: Uuid;
    fieldMappings: Array<EventContainerMovementDependentEventFieldMapping>;
};

export type EventContainerMovementOperationConfiguration = {
    actorUserTemplateIds?: Array<Uuid>;
    allowedFieldCombinations: Array<Array<Uuid>>;
    assignmentColumnIds: Array<Uuid>;
    businessStatusRule?: EventContainerMovementBusinessStatusRule;
    dependentEvents: Array<EventContainerMovementDependentEventConfiguration>;
    displayName: string;
    eligibleEventContainerSystemStatuses: Array<OttopilotEventContainerStatus>;
    eventContainerTemplateId: Uuid;
    frontend: {
        assignmentFieldLabels?: Record<string, string>;
        displayOrder?: number;
        sourceLabel?: string;
        targetLabel?: string;
        transferDimensionId?: string;
    };
    id: Uuid;
    organizationalUnit?: {
        allowedTargetOrganizationalUnitIds?: Array<Uuid>;
        allowedTargetOrganizationalUnitTemplateIds: Array<Uuid>;
        hierarchyFields: Array<EventContainerMovementHierarchyField>;
        scope: EventContainerMovementOuScope;
    };
    reason: {
        allowedValues?: Array<string>;
        required: boolean;
    };
    remarks: {
        required: boolean;
    };
    targetUserEligibility: {
        organizationalUnitMembership: EventContainerMovementTargetUserOuMembership;
        userTemplateIds: Array<Uuid>;
    };
    transferAssignmentColumnId?: Uuid;
};

export type EventContainerMovementWorkspaceCapabilityConfiguration = {
    operations: Array<EventContainerMovementOperationConfiguration>;
};

const businessStatusRuleValidator: zod.ZodType<EventContainerMovementBusinessStatusRule> = zod.strictObject({
    columnId: uuidZodValidator,
    eligibleValues: uuidZodValidator.array().min(1),
});

const movementFieldValidator = uuidZodValidator;

const dependentEventConfigurationValidator: zod.ZodType<EventContainerMovementDependentEventConfiguration> = zod
    .strictObject({
        businessStatusRule: businessStatusRuleValidator.optional(),
        eligibleSystemStatuses: zod.enum(OttopilotEventStatus).array().min(1),
        eventTemplateId: uuidZodValidator,
        fieldMappings: zod
            .strictObject({
                eventColumnId: uuidZodValidator,
                movementField: movementFieldValidator,
            })
            .array()
            .min(1),
    })
    .superRefine((configuration, context) => {
        if (configuration.eligibleSystemStatuses.some((status) => status !== OttopilotEventStatus.active)) {
            context.addIssue({
                code: "custom",
                message: "Historical or archived dependent Events cannot be configured as eligible for movement.",
                path: ["eligibleSystemStatuses"],
            });
        }
    });

export const eventContainerMovementOperationConfigurationValidator: zod.ZodType<EventContainerMovementOperationConfiguration> = zod
    .strictObject({
        actorUserTemplateIds: uuidZodValidator.array().min(1).optional(),
        allowedFieldCombinations: movementFieldValidator.array().min(1).array().min(1),
        assignmentColumnIds: uuidZodValidator.array(),
        businessStatusRule: businessStatusRuleValidator.optional(),
        dependentEvents: dependentEventConfigurationValidator.array(),
        displayName: zod.string().trim().min(1),
        eligibleEventContainerSystemStatuses: zod.enum(OttopilotEventContainerStatus).array().min(1),
        eventContainerTemplateId: uuidZodValidator,
        frontend: zod.strictObject({
            assignmentFieldLabels: zod.record(zod.string(), zod.string().trim().min(1)).optional(),
            displayOrder: zod.number().int().nonnegative().optional(),
            sourceLabel: zod.string().trim().min(1).optional(),
            targetLabel: zod.string().trim().min(1).optional(),
            transferDimensionId: zod.string().trim().min(1).optional(),
        }),
        id: uuidZodValidator,
        organizationalUnit: zod
            .strictObject({
                allowedTargetOrganizationalUnitIds: uuidZodValidator.array().min(1).optional(),
                allowedTargetOrganizationalUnitTemplateIds: uuidZodValidator.array().min(1),
                hierarchyFields: zod
                    .strictObject({
                        eventContainerColumnId: uuidZodValidator,
                        organizationalUnitTemplateId: uuidZodValidator,
                    })
                    .array(),
                scope: zod.enum(EventContainerMovementOuScope),
            })
            .optional(),
        reason: zod.strictObject({
            allowedValues: zod.string().trim().min(1).array().min(1).optional(),
            required: zod.boolean(),
        }),
        remarks: zod.strictObject({required: zod.boolean()}),
        targetUserEligibility: zod.strictObject({
            organizationalUnitMembership: zod.enum(EventContainerMovementTargetUserOuMembership),
            userTemplateIds: uuidZodValidator.array().min(1),
        }),
        transferAssignmentColumnId: uuidZodValidator.optional(),
    })
    .superRefine((operation, context) => {
        if (operation.eligibleEventContainerSystemStatuses.some((status) => status !== OttopilotEventContainerStatus.active)) {
            context.addIssue({
                code: "custom",
                message: "Historical or archived Event Containers cannot be configured as eligible for movement.",
                path: ["eligibleEventContainerSystemStatuses"],
            });
        }
        const allowedAssignmentColumnIds = new Set(operation.assignmentColumnIds);
        for (const configuredColumnId of Object.keys(operation.frontend.assignmentFieldLabels ?? {})) {
            const parsedColumnId = uuidZodValidator.safeParse(configuredColumnId);
            if (parsedColumnId.success !== true || !allowedAssignmentColumnIds.has(parsedColumnId.data)) {
                context.addIssue({
                    code: "custom",
                    message: "Frontend assignment labels may only reference configured assignment columns.",
                    path: ["frontend", "assignmentFieldLabels", configuredColumnId],
                });
            }
        }
        const nonAssignmentMovementFields = new Set<Uuid>();
        for (const [combinationIndex, combination] of operation.allowedFieldCombinations.entries()) {
            if (new Set(combination).size !== combination.length) {
                context.addIssue({
                    code: "custom",
                    message: "Movement field combinations cannot contain duplicate fields.",
                    path: ["allowedFieldCombinations", combinationIndex],
                });
            }
            for (const field of combination) {
                if (!allowedAssignmentColumnIds.has(field)) {
                    nonAssignmentMovementFields.add(field);
                }
            }
        }
        if (nonAssignmentMovementFields.size > 1) {
            context.addIssue({
                code: "custom",
                message: "Movement field combinations may reference at most one non-assignment field: the template-configured Organizational Unit column.",
                path: ["allowedFieldCombinations"],
            });
        }
        const permitsOrganizationalUnitChange = nonAssignmentMovementFields.size === 1;
        if (permitsOrganizationalUnitChange && operation.organizationalUnit === undefined) {
            context.addIssue({
                code: "custom",
                message: "Operations that transfer Event Containers must configure organizationalUnit behavior.",
                path: ["organizationalUnit"],
            });
        }
        if (permitsOrganizationalUnitChange && operation.transferAssignmentColumnId === undefined) {
            context.addIssue({
                code: "custom",
                message: "Operations that transfer Event Containers must configure transferAssignmentColumnId.",
                path: ["transferAssignmentColumnId"],
            });
        }
        if (operation.transferAssignmentColumnId !== undefined && !allowedAssignmentColumnIds.has(operation.transferAssignmentColumnId)) {
            context.addIssue({
                code: "custom",
                message: "transferAssignmentColumnId must be one of assignmentColumnIds.",
                path: ["transferAssignmentColumnId"],
            });
        }
    });

export const eventContainerMovementWorkspaceCapabilityConfigurationValidator: zod.ZodType<EventContainerMovementWorkspaceCapabilityConfiguration> = zod
    .strictObject({
        operations: eventContainerMovementOperationConfigurationValidator.array().min(1),
    })
    .superRefine((configuration, context) => {
        const operationIds = new Set<Uuid>();
        configuration.operations.forEach((operation, index) => {
            if (operationIds.has(operation.id)) {
                context.addIssue({
                    code: "custom",
                    message: "Movement operation IDs must be unique.",
                    path: ["operations", index, "id"],
                });
            }
            operationIds.add(operation.id);
        });
    });
